import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Metric {
  id: string
  name: string
  value: number
  target: number
  unit: string
  trend: string
  change: number
}

interface Milestone {
  id: string
  customer: string
  milestone: string
  status: string
  target_date: string
  progress: number
  owner: string
}

interface Playbook {
  id: string
  name: string
  trigger: string
  steps: number
  completion_rate: number
  avg_time_days: number
  success_rate: number
}

interface Statistics {
  active_customers: number
  avg_health_score: number
  expansion_revenue: number
  nrr: number
  time_to_value: number
  adoption_rate: number
}

export default function CustomerSuccessPage() {
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [playbooks, setPlaybooks] = useState<Playbook[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [metricsRes, milestonesRes, playbooksRes, statsRes] = await Promise.all([
        fetch('/api/customersuccess/metrics'),
        fetch('/api/customersuccess/milestones'),
        fetch('/api/customersuccess/playbooks'),
        fetch('/api/customersuccess/statistics')
      ])
      if (metricsRes.ok) setMetrics(await metricsRes.json())
      if (milestonesRes.ok) setMilestones(await milestonesRes.json())
      if (playbooksRes.ok) setPlaybooks(await playbooksRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching customer success data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800'
    if (status === 'in_progress') return 'bg-yellow-100 text-yellow-800'
    if (status === 'at_risk') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return '↑'
    if (trend === 'down') return '↓'
    return '→'
  }

  const getTrendColor = (trend: string, isPositive: boolean = true) => {
    if (trend === 'up') return isPositive ? 'text-green-600' : 'text-red-600'
    if (trend === 'down') return isPositive ? 'text-red-600' : 'text-green-600'
    return 'text-gray-600'
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-green-500'
    if (progress >= 50) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Customer Success</h1>
          <p className="mt-2 text-gray-600">Phase 229 - Customer success management and tracking</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Customers</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active_customers}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Health Score</p>
              <p className="text-2xl font-bold text-green-600">{statistics.avg_health_score}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Expansion Revenue</p>
              <p className="text-2xl font-bold text-blue-600">${statistics.expansion_revenue.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">NRR</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.nrr}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Time to Value</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.time_to_value} days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Adoption Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.adoption_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Key Metrics</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {metrics.map((metric) => (
              <div key={metric.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm">{metric.name}</span>
                  <span className={`text-sm ${getTrendColor(metric.trend)}`}>
                    {getTrendIcon(metric.trend)} {metric.change > 0 ? '+' : ''}{metric.change}%
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-gray-900">{metric.value}</span>
                  <span className="text-gray-500 text-sm mb-1">{metric.unit}</span>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Progress</span>
                    <span>{metric.target} target</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-2 rounded-full ${getProgressColor((metric.value / metric.target) * 100)}`}
                      style={{ width: `${Math.min(100, (metric.value / metric.target) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Customer Milestones</h2>
            </div>
            <div className="p-6 space-y-4">
              {milestones.map((milestone) => (
                <div key={milestone.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{milestone.customer}</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(milestone.status)}`}>
                      {milestone.status}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{milestone.milestone}</p>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Owner: {milestone.owner}</span>
                    <span className="text-gray-500">Due: {milestone.target_date}</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-2 rounded-full ${getProgressColor(milestone.progress)}`}
                      style={{ width: `${milestone.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{milestone.progress}% complete</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Success Playbooks</h2>
            </div>
            <div className="p-6 space-y-4">
              {playbooks.map((playbook) => (
                <div key={playbook.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{playbook.name}</span>
                    <span className="text-green-600 font-medium">{playbook.success_rate}% success</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">Trigger: {playbook.trigger}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Steps</p>
                      <p className="font-medium text-gray-900">{playbook.steps}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Completion</p>
                      <p className="font-medium text-blue-600">{playbook.completion_rate}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Avg Time</p>
                      <p className="font-medium text-purple-600">{playbook.avg_time_days}d</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
